
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Inquiry {
  id: string;
  name: string;
  phone: string;
  subject: string;
  message: string;
  admin_reply: string | null;
  replied_at: string | null;
  is_public: boolean;
  created_at: string;
}

export default function InquirySection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [publicInquiries, setPublicInquiries] = useState<Inquiry[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    fetchPublicInquiries();
  }, []);

  const fetchPublicInquiries = async () => {
    const { data } = await supabase
      .from('inquiries')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .limit(10);
    
    if (data) {
      setPublicInquiries(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject || !formData.message) {
      alert('이름, 제목, 내용을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    const { error } = await supabase
      .from('inquiries')
      .insert([{
        name: formData.name,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      }]);

    setIsSubmitting(false);

    if (error) {
      alert('문의 등록에 실패했습니다. 다시 시도해주세요.');
      return;
    }

    setSubmitSuccess(true);
    setFormData({ name: '', phone: '', subject: '', message: '' });
    
    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section id="inquiry" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-[#f8f9fa] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-teal-50 text-teal-600 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            Contact Us
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            문의하기
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            궁금한 점이 있으시면 언제든지 문의해주세요. 빠르게 답변드리겠습니다.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          {/* Inquiry Form */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-6 md:p-8">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <span className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-teal-100 rounded-full mr-2 sm:mr-3">
                <i className="ri-edit-line text-teal-600 text-sm sm:text-lg"></i>
              </span>
              문의 작성
            </h3>

            {submitSuccess ? (
              <div className="text-center py-8 sm:py-12">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-check-line text-green-600 text-xl sm:text-3xl"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2">문의가 등록되었습니다!</h4>
                <p className="text-sm sm:text-base text-gray-600">빠른 시일 내에 답변드리겠습니다.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="이름을 입력해주세요"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    연락처 (선택)
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="전화번호 또는 이메일"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    제목 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="문의 제목을 입력해주세요"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    문의 내용 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                    placeholder="문의 내용을 자세히 작성해주세요"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                  />
                  <p className="text-[10px] sm:text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 sm:py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg sm:rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      등록 중...
                    </span>
                  ) : (
                    '문의하기'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Public Q&A */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <span className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full mr-2 sm:mr-3">
                <i className="ri-question-answer-line text-amber-600 text-sm sm:text-lg"></i>
              </span>
              공개 문의 & 답변
            </h3>

            {publicInquiries.length === 0 ? (
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className="ri-chat-3-line text-gray-400 text-xl sm:text-2xl"></i>
                </div>
                <p className="text-sm sm:text-base text-gray-500">아직 공개된 문의가 없습니다.</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {publicInquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    className="bg-white rounded-lg sm:rounded-xl shadow-md overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedId(expandedId === inquiry.id ? null : inquiry.id)}
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <span className={`flex-shrink-0 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium mr-2 sm:mr-3 ${
                          inquiry.admin_reply 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {inquiry.admin_reply ? '답변완료' : '대기중'}
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {inquiry.subject}
                        </span>
                      </div>
                      <i className={`ri-arrow-down-s-line text-gray-400 text-lg sm:text-xl transition-transform flex-shrink-0 ml-2 ${
                        expandedId === inquiry.id ? 'rotate-180' : ''
                      }`}></i>
                    </button>

                    {expandedId === inquiry.id && (
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-100">
                        {/* Question */}
                        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center mb-1.5 sm:mb-2">
                            <span className="text-[10px] sm:text-xs font-medium text-gray-500">
                              {inquiry.name} · {formatDate(inquiry.created_at)}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                            {inquiry.message}
                          </p>
                        </div>

                        {/* Answer */}
                        {inquiry.admin_reply && (
                          <div className="mt-2 sm:mt-3 p-3 sm:p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                            <div className="flex items-center mb-1.5 sm:mb-2">
                              <span className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center bg-teal-600 rounded-full mr-1.5 sm:mr-2">
                                <i className="ri-admin-line text-white text-[10px] sm:text-xs"></i>
                              </span>
                              <span className="text-[10px] sm:text-xs font-medium text-teal-700">
                                관리자 답변 · {inquiry.replied_at ? formatDate(inquiry.replied_at) : ''}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-teal-800 whitespace-pre-wrap">
                              {inquiry.admin_reply}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
